import { HeldWorkflowView, HoldRuleView } from '@/console/api/types'

/**
 * `GET /workflow/holds` 응답을 Hold 관리 표의 행으로 편다. 계산은 전부 여기 —
 * `HoldRuleTable` 은 이 행을 그대로 그리기만 한다(브리프 원칙).
 */
export type HoldRuleRow = {
  key: string
  holdId: string
  typeLabel: string
  tone: 'crit' | 'neutral'
  target: string
  period: string
  reason: string
  expired: boolean
  autoGenerated: boolean
}

const TYPE_LABELS: Record<HoldRuleView['type'], { label: string; tone: 'crit' | 'neutral' }> = {
  maintenance: { label: '전건 정지', tone: 'crit' },
  attr: { label: '속성', tone: 'neutral' },
  predicate: { label: '조건식', tone: 'neutral' },
}

/** `nike-month-end-hold-` 로 시작하는 규칙은 스케줄러 자동 생성이라 삭제를 막는다(핸드오프 §4.1 함정). */
export const isAutoGeneratedHold = (holdId: string): boolean => holdId.startsWith('nike-month-end-hold-')

const seoulFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const formatMoment = (epochMillis: number): string => {
  const parts = seoulFormatter.formatToParts(epochMillis)
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`
}

/** `attr`/`predicate` 는 `until`, `maintenance` 는 `toExclusive` 가 종료 시각이다. */
const endOf = (rule: HoldRuleView): number | undefined => (rule.type === 'maintenance' ? rule.toExclusive : rule.until)

/** `until`(또는 `toExclusive`) 이 있고 지금보다 과거면 만료다. 없으면(무기한) 만료가 아니다. */
export const isHoldExpired = (rule: HoldRuleView, now: number): boolean => {
  const end = endOf(rule)
  return end !== undefined && end < now
}

/**
 * `MM-DD HH:mm → MM-DD HH:mm`. 시작이 없으면 `즉시`, 끝이 없으면 `무기한`.
 * `now` 는 문구 계산에 쓰이지 않는다(만료 판정은 `isHoldExpired` 가 따로 한다) — 그래도
 * 브리프가 못박은 시그니처(`formatHoldPeriod(rule, now)`)를 그대로 유지한다.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const formatHoldPeriod = (rule: HoldRuleView, now: number): string => {
  const end = endOf(rule)
  const from = rule.from !== undefined ? formatMoment(rule.from) : '즉시'
  const until = end !== undefined ? formatMoment(end) : '무기한'
  return `${from} → ${until}`
}

/**
 * 표의 "적용 대상" 열 — **도메인과 조건을 둘 다** 보여준다.
 *
 * PR 리뷰(봇·사람 합쳐 4회 지적) — 예전에는 `domain === '*'` 를 가장 먼저 검사해 `'모든 도메인'`
 * 으로 끝냈다. 그래서 `{ domain:'*', type:'attr', field:'shipper.shipperCode', values:['NIKE'] }`
 * 처럼 **전 도메인에서 특정 화주만** 멈추는 규칙이 표에 "모든 도메인" 으로만 찍혔다 — 전건 정지와
 * 구분이 안 됐다. `HoldCreateForm` 이 domain `*` + 속성/조건식 조합을 허용하므로 실제로 생기는
 * 케이스이고, 해제 확인 다이얼로그도 같은 값을 쓰기 때문에 "NIKE 주문만 멈추는 규칙" 을 지우면서
 * "전 주문을 멈추는 규칙" 을 지운다고 읽게 된다.
 *
 * 조건이 없는 `maintenance`(전건 정지)만 도메인 단독으로 충분하다. 표에 도메인 전용 열이 없어
 * `attr`/`predicate` 도 도메인을 함께 낸다 — 예전에는 `*` 가 아닌 규칙의 도메인이 아예 안 보였다.
 */
export const describeHoldTarget = (rule: HoldRuleView): string => {
  const domainPart = rule.domain === '*' ? '모든 도메인' : rule.domain

  if (rule.type === 'attr') {
    return `${domainPart} · ${rule.field ?? ''} ∈ ${(rule.values ?? []).join(' · ')}`
  }

  if (rule.type === 'predicate') {
    const count = rule.predicate && 'nodes' in rule.predicate ? rule.predicate.nodes.length : 1
    return `${domainPart} · 조건식 ${count}개`
  }

  return domainPart
}

export const toHoldRuleRows = (rules: HoldRuleView[], now: number): HoldRuleRow[] =>
  rules.map((rule) => {
    const typeMeta = TYPE_LABELS[rule.type]
    return {
      key: rule.holdId,
      holdId: rule.holdId,
      typeLabel: typeMeta.label,
      tone: typeMeta.tone,
      target: describeHoldTarget(rule),
      period: formatHoldPeriod(rule, now),
      reason: rule.reason ?? '',
      expired: isHoldExpired(rule, now),
      autoGenerated: isAutoGeneratedHold(rule.holdId),
    }
  })

/** 멈춘 워크플로 목록의 행. `HeldWorkflowList` 는 이 행을 `RowList` 로 그리기만 한다. */
export type HeldWorkflowRow = { key: string; label: string; typeLabel: string; elapsed: string }

/** `waitStartedAt` 이 없으면(구버전 응답) 대시로 낸다. */
export const formatElapsedSince = (waitStartedAt: string | undefined, now: number): string => {
  if (!waitStartedAt) {
    return '-'
  }

  const diffMs = Math.max(0, now - Date.parse(waitStartedAt))
  const hours = Math.floor(diffMs / 3_600_000)
  const minutes = Math.floor((diffMs % 3_600_000) / 60_000)
  return `${hours}h ${minutes}m`
}

// 2단계에 갚아야 할 빚(코드 리뷰 Finding 2, LOGISPRD-12506 Task 6): 지금은 모든 워크플로를 그대로
// 편다 — 필터링을 하지 않는다. `/subscribers` 가 정책 Hold 외 다른 wait 도 섞어 준다면
// workflowType 으로 거를 계획이지만(핸드오프 §4.1 함정), 1단계엔 거를 실제 응답이 없어 기준을
// 지어내지 않았다. 2단계에서 실 응답을 보고 기준을 정하고 이 함수에서 필터링해야 한다.
export const toHeldWorkflowRows = (workflows: HeldWorkflowView[], now: number): HeldWorkflowRow[] =>
  workflows.map((workflow) => ({
    key: workflow.workflowId,
    label: `${workflow.orderId ?? '-'} / ${workflow.orderItemId ?? '-'}`,
    typeLabel: workflow.workflowType,
    elapsed: formatElapsedSince(workflow.waitStartedAt, now),
  }))
